import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { Card, CardBody } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { ProgressBar } from "@/ui/ProgressBar";
import { LoadingState, EmptyState } from "@/ui/feedback/states";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { useLabels } from "@/lib/labels";
import { isAgencyAdmin, MOCK_USER } from "@/lib/permissions";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { useAuthStore } from "@/features/auth/auth.store";
import type { Priority, Task, TaskStatus } from "@/types";
import { ClientCombobox } from "@/ui/ClientCombobox";
import { useClients } from "@/features/clients/clients.hooks";
import { useTask, useUpdateTask, useUpdateTaskStatus, useUpdateProgress, useArchiveTask } from "./tasks.hooks";
import { getEffectiveProgress, getTaskProgressTone, getTaskProgressLabelKey, type TaskInput } from "./tasks.adapter";

export function TaskDetailsPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const authStatus = useAuthStore((s) => s.status);
  const user = currentUser ?? (authStatus === "anonymous" ? MOCK_USER : null);
  const admin = user && isAgencyAdmin(user);

  const { task, isLoading, isError } = useTask(taskId ?? "");

  if (isLoading) return <PageContainer><LoadingState rows={6} /></PageContainer>;
  if (isError || !task) return <PageContainer><EmptyState title={t("tasks.notFound")} message="" /></PageContainer>;

  return admin ? <AdminTaskDetails task={task} /> : <MemberTaskDetails task={task} />;
}

// ─── Admin view ────────────────────────────────────────────────────────────────

function AdminTaskDetails({ task }: { task: Task }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { TASK_STATUS, PRIORITY } = useLabels();
  const archive = useArchiveTask();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const onArchive = async () => {
    try {
      await archive.mutateAsync(task.id);
      toast.success(t("tasks.archived"));
      navigate("/tasks");
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.archiveFailed")));
    } finally {
      setArchiveOpen(false);
    }
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="xs" onClick={() => navigate("/tasks")}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {t("tasks.backToTasks")}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> {t("common.edit")}
          </Button>
          <Button variant="ghost" size="sm" className="text-danger" onClick={() => setArchiveOpen(true)}>
            <Trash2 className="h-3.5 w-3.5 mr-1" /> {t("common.archive")}
          </Button>
        </div>
      </div>

      <TaskHeader
        task={task}
        statusLabel={TASK_STATUS[task.status].label}
        statusTone={TASK_STATUS[task.status].tone}
        priorityLabel={PRIORITY[task.priority].label}
        priorityTone={PRIORITY[task.priority].tone}
        progressTone={getTaskProgressTone(task)}
        progressLabelKey={getTaskProgressLabelKey(task.status)}
      />
      <TaskBrief task={task} />

      <EditTaskModal open={editOpen} task={task} onClose={() => setEditOpen(false)} />
      <ConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title={t("tasks.archiveTitle")}
        message={t("tasks.archiveMessage", { title: task.title })}
        confirmLabel={t("common.archive")}
        onConfirm={onArchive}
      />
    </PageContainer>
  );
}

// ─── Member view ───────────────────────────────────────────────────────────────

function MemberTaskDetails({ task }: { task: Task }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { TASK_STATUS, PRIORITY } = useLabels();
  const updateStatus = useUpdateTaskStatus();
  const updateProgress = useUpdateProgress();
  const [progressInput, setProgressInput] = useState(String(getEffectiveProgress(task)));

  const onStatus = async (status: TaskStatus, progress?: number) => {
    try {
      await updateStatus.mutateAsync({ id: task.id, status, ...(progress !== undefined ? { progress } : {}) });
      toast.success(t("tasks.statusUpdated"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.updateStatusFailed")));
    }
  };

  const onProgressSubmit = async () => {
    const n = Math.max(0, Math.min(100, Number(progressInput)));
    try {
      await updateProgress.mutateAsync({ id: task.id, progress: n });
      toast.success(t("tasks.progress.updated"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.updateStatusFailed")));
    }
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="xs" onClick={() => navigate("/tasks")}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {t("tasks.backToTasks")}
        </Button>
        <div className="ml-auto flex items-center gap-1">
          {task.status === "DONE" ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => onStatus("IN_PROGRESS", 90)}>{t("tasks.reopen")}</Button>
              <Button variant="ghost" size="sm" className="text-warning" onClick={() => onStatus("ON_HOLD", 90)}>{t("tasks.reportIssue")}</Button>
            </>
          ) : (
            <>
              {task.status !== "IN_PROGRESS" && (
                <Button variant="ghost" size="sm" onClick={() => onStatus("IN_PROGRESS")}>{t("tasks.markInProgress")}</Button>
              )}
              {task.status !== "ON_HOLD" && (
                <Button variant="ghost" size="sm" className="text-warning" onClick={() => onStatus("ON_HOLD")}>{t("tasks.markBlocked")}</Button>
              )}
              <Button variant="ghost" size="sm" className="text-success" onClick={() => onStatus("DONE")}>{t("tasks.markDone")}</Button>
            </>
          )}
        </div>
      </div>

      {task.status === "DONE" && (
        <div className="mb-4 rounded-sm border border-success/20 bg-success-dim px-4 py-3 text-[13px] text-success font-semibold">
          {t("tasks.progress.completed")}
        </div>
      )}

      <TaskHeader
        task={task}
        statusLabel={TASK_STATUS[task.status].label}
        statusTone={TASK_STATUS[task.status].tone}
        priorityLabel={PRIORITY[task.priority].label}
        priorityTone={PRIORITY[task.priority].tone}
      />

      {task.status !== "DONE" && (
        <Card className="mb-4">
          <CardBody>
            <div className="flex items-center gap-3">
              <span className="text-[12px] text-text2 font-medium">{t("tasks.columns.progress")}</span>
              <input
                type="number"
                min={0}
                max={100}
                value={progressInput}
                onChange={(e) => setProgressInput(e.target.value)}
                className="w-16 rounded border border-border bg-bg px-2 py-1 text-[12px] text-text text-center"
              />
              <span className="text-[12px] text-text2">%</span>
              <Button variant="ghost" size="xs" onClick={onProgressSubmit} disabled={updateProgress.isPending}>
                {t("tasks.progress.update")}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <TaskBrief task={task} />
    </PageContainer>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function TaskHeader({ task, statusLabel, statusTone, priorityLabel, priorityTone, progressTone, progressLabelKey }: {
  task: Task;
  statusLabel: string;
  statusTone: string;
  priorityLabel: string;
  priorityTone: string;
  progressTone?: "primary" | "success" | "warning" | "danger";
  progressLabelKey?: string;
}) {
  const { t } = useTranslation();
  return (
    <Card className="mb-4">
      <CardBody>
        <h1 className="text-xl font-display font-bold text-text mb-3">{task.title}</h1>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Badge tone={statusTone as "default"}>{statusLabel}</Badge>
          <Badge tone={priorityTone as "default"} dot>{priorityLabel}</Badge>
          {task.clientName && <Badge tone="info">{task.clientName}</Badge>}
          {task.assignee && <span className="text-[12px] text-text2">{task.assignee}</span>}
          {task.dueDate && <span className="text-[12px] text-text3">{t("tasks.columns.due")}: {task.dueDate}</span>}
        </div>
        <div className="w-48">
          <ProgressBar value={getEffectiveProgress(task)} tone={progressTone ?? "primary"} />
          {progressLabelKey && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-text3">{t(progressLabelKey)}</span>
              <span className="text-[10px] text-text3">{getEffectiveProgress(task)}%</span>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function TaskBrief({ task }: { task: Task }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardBody>
        <h2 className="text-[13px] font-semibold text-text2 uppercase tracking-wide mb-4">{t("tasks.brief")}</h2>
        <div className="grid gap-5">
          <BriefSection label={t("tasks.form.description")} content={task.description} emptyKey="tasks.noDescription" />
          <BriefSection label={t("tasks.form.instructions")} content={task.instructions} emptyKey="tasks.noInstructions" />
          <BriefSection label={t("tasks.form.expectedResult")} content={task.expectedResult} emptyKey="tasks.noExpectedResult" />
          <BriefSection label={t("tasks.form.doNotDo")} content={task.doNotDo} emptyKey="tasks.noDoNotDo" />
        </div>
      </CardBody>
    </Card>
  );
}

function BriefSection({ label, content, emptyKey }: { label: string; content?: string; emptyKey: string }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="text-[11px] font-semibold text-text3 uppercase tracking-wide mb-1">{label}</div>
      {content
        ? <p className="text-[13px] text-text whitespace-pre-wrap leading-relaxed">{content}</p>
        : <span className="text-[12px] text-text3 italic">{t(emptyKey)}</span>
      }
    </div>
  );
}

// ─── Edit modal (admin only) ──────────────────────────────────────────────────

function EditTaskModal({ open, task, onClose }: { open: boolean; task: Task; onClose: () => void }) {
  const { t } = useTranslation();
  const { PRIORITY, TASK_STATUS } = useLabels();
  const updateTask = useUpdateTask();
  const { clients } = useClients();

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [instructions, setInstructions] = useState(task.instructions ?? "");
  const [expectedResult, setExpectedResult] = useState(task.expectedResult ?? "");
  const [doNotDo, setDoNotDo] = useState(task.doNotDo ?? "");
  const [clientId, setClientId] = useState(task.clientId ?? "");
  const [priority, setPriority] = useState<Priority>(task.priority);
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [dueDate, setDueDate] = useState(task.dueDate ?? "");
  const [error, setError] = useState("");

  const submit = async () => {
    if (!title.trim()) { setError(t("tasks.form.titleRequired")); return; }
    const selectedClient = clients.find((c) => c.id === clientId);
    const input: TaskInput = {
      title: title.trim(),
      description: description || undefined,
      instructions: instructions || undefined,
      expectedResult: expectedResult || undefined,
      doNotDo: doNotDo || undefined,
      clientId: clientId || undefined,
      clientName: selectedClient?.name,
      priority,
      status,
      dueDate: dueDate || undefined,
    };
    try {
      await updateTask.mutateAsync({ id: task.id, input });
      toast.success(t("common.save"));
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.updateStatusFailed")));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("common.edit")}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>{t("common.cancel")}</Button>
          <Button size="sm" onClick={submit} disabled={updateTask.isPending}>{t("common.save")}</Button>
        </>
      }
    >
      <Field label={t("tasks.form.title")} required error={error}>
        <input className={fieldInputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={t("tasks.form.description")}>
        <textarea className={fieldInputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label={t("tasks.form.instructions")}>
        <textarea className={fieldInputClass} rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      </Field>
      <Field label={t("tasks.form.expectedResult")}>
        <textarea className={fieldInputClass} rows={3} value={expectedResult} onChange={(e) => setExpectedResult(e.target.value)} />
      </Field>
      <Field label={t("tasks.form.doNotDo")}>
        <textarea className={fieldInputClass} rows={3} value={doNotDo} onChange={(e) => setDoNotDo(e.target.value)} />
      </Field>
      <Field label={t("tasks.form.client")}>
        <ClientCombobox
          options={clients}
          value={clientId}
          onChange={(id) => setClientId(id)}
          nullable
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("tasks.form.priority")} required>
          <select className={fieldInputClass} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {(Object.keys(PRIORITY) as Priority[]).map((p) => <option key={p} value={p}>{PRIORITY[p].label}</option>)}
          </select>
        </Field>
        <Field label={t("tasks.form.status")} required>
          <select className={fieldInputClass} value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
            {(Object.keys(TASK_STATUS) as TaskStatus[]).map((s) => <option key={s} value={s}>{TASK_STATUS[s].label}</option>)}
          </select>
        </Field>
      </div>
      <Field label={t("tasks.form.dueDate")}>
        <input type="date" className={fieldInputClass} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </Field>
    </Modal>
  );
}
